import React, { useState } from "react";
import { Alert, Button, Checkbox, Divider, Modal, Radio, Space, Typography } from "antd";
import {
  HOMEWORK_RESOLUTION_ACTION,
  HomeworkResolutionAction,
  HomeworkResolutionDto,
  ResourceHomeworkImpact,
} from "../../types/api/resourceHomeworkImpact";
import HomeworkImpactDescriptions from "./HomeworkImpactDescriptions";

const { Paragraph, Text, Title } = Typography;

interface ResourceHomeworkImpactModalProps {
  open: boolean;
  impact: ResourceHomeworkImpact[];
  confirmLoading?: boolean;
  onCancel: () => void;
  onConfirm: (resolutions: HomeworkResolutionDto[]) => void;
}

interface ResolutionDraft {
  action: HomeworkResolutionAction | null;
  // SCRUM-1590: null until the admin says whether the replacement is the
  // same exercise or different content.
  sameExercise: boolean | null;
  acknowledgeInFlight: boolean;
}

const emptyDraft = (): ResolutionDraft => ({
  action: null,
  sameExercise: null,
  acknowledgeInFlight: false,
});

/**
 * Why the replacement cannot be the same exercise, or null when it can. The
 * backend refuses the same cases (NOT_SAME_EXERCISE).
 */
const notSameExerciseReason = (
  resource: ResourceHomeworkImpact,
): string | null => {
  if (resource.resource_type_matches === false) {
    return "El recurso nuevo es de otro tipo, así que no puede ser el mismo ejercicio.";
  }
  if (resource.game_quiz_id_matches === false) {
    return "El recurso nuevo tiene otro quiz, así que no puede ser el mismo ejercicio.";
  }
  return null;
};

const isDraftComplete = (
  resource: ResourceHomeworkImpact,
  draft: ResolutionDraft,
): boolean => {
  if (!draft.action) return false;
  if (draft.action === HOMEWORK_RESOLUTION_ACTION.REPOINT) {
    if (draft.sameExercise === null) return false;
    if (draft.sameExercise && notSameExerciseReason(resource)) return false;
  }
  if (resource.in_flight_attempt_count > 0 && !draft.acknowledgeInFlight) {
    return false;
  }
  return true;
};

const buildResolution = (
  resource: ResourceHomeworkImpact,
  draft: ResolutionDraft,
): HomeworkResolutionDto => {
  const resolution: HomeworkResolutionDto = {
    resource_id: resource.resource_id,
    action: draft.action as HomeworkResolutionAction,
  };
  if (draft.action === HOMEWORK_RESOLUTION_ACTION.REPOINT) {
    resolution.new_resource_id = resource.replacement_resource_id as number;
    resolution.same_exercise_assertion = draft.sameExercise === true;
  }
  if (resource.in_flight_attempt_count > 0) {
    resolution.acknowledge_in_flight = draft.acknowledgeInFlight;
  }
  return resolution;
};

/**
 * Shown on a 409 from a lesson save or a resource delete when the affected
 * resource(s) still have live homeworks. Modeled on
 * `GameQuizzes/DeleteGameQuizModal`: the admin sees exactly what is at stake
 * and must make an explicit, per-resource choice before anything is saved.
 * Cancelling persists nothing.
 */
const ResourceHomeworkImpactModal: React.FC<ResourceHomeworkImpactModalProps> = ({
  open,
  impact,
  confirmLoading = false,
  onCancel,
  onConfirm,
}) => {
  const [drafts, setDrafts] = useState<Record<number, ResolutionDraft>>({});

  const draftFor = (resourceId: number): ResolutionDraft =>
    drafts[resourceId] ?? emptyDraft();

  const updateDraft = (resourceId: number, patch: Partial<ResolutionDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [resourceId]: { ...draftFor(resourceId), ...patch },
    }));
  };

  const handleActionChange = (
    resource: ResourceHomeworkImpact,
    action: HomeworkResolutionAction,
  ) => {
    updateDraft(resource.resource_id, {
      action,
      sameExercise: null,
    });
  };

  const canConfirm =
    impact.length > 0 &&
    impact.every((resource) => isDraftComplete(resource, draftFor(resource.resource_id)));

  const handleConfirm = () => {
    const resolutions = impact.map((resource) =>
      buildResolution(resource, draftFor(resource.resource_id)),
    );
    onConfirm(resolutions);
  };

  return (
    <Modal
      open={open}
      title="Este recurso tiene tareas asignadas"
      onCancel={onCancel}
      width={720}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button
          key="confirm"
          type="primary"
          disabled={!canConfirm}
          loading={confirmLoading}
          onClick={handleConfirm}
        >
          Confirmar
        </Button>,
      ]}
    >
      <Paragraph type="secondary">
        Elegí qué hacer con cada recurso antes de continuar. No se guarda nada
        hasta que confirmes.
      </Paragraph>
      {impact.map((resource, index) => {
        const draft = draftFor(resource.resource_id);
        const canRepoint = resource.replacement_resource_id !== null;
        return (
          <div key={resource.resource_id}>
            {index > 0 && <Divider />}
            <Title level={5}>{resource.resource_title}</Title>
            <HomeworkImpactDescriptions counts={resource} />

            <Radio.Group
              style={{ marginTop: 12 }}
              value={draft.action}
              onChange={(e) => handleActionChange(resource, e.target.value)}
            >
              {canRepoint && (
                <Radio value={HOMEWORK_RESOLUTION_ACTION.REPOINT}>
                  Reasignar al nuevo recurso
                </Radio>
              )}
              <Radio value={HOMEWORK_RESOLUTION_ACTION.WITHDRAW}>
                Quitar tareas
              </Radio>
            </Radio.Group>

            {draft.action === HOMEWORK_RESOLUTION_ACTION.REPOINT && (
              <div style={{ marginTop: 8 }}>
                <Paragraph style={{ marginBottom: 4 }}>
                  ¿El recurso nuevo es el mismo ejercicio?
                </Paragraph>
                {notSameExerciseReason(resource) && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message={notSameExerciseReason(resource)}
                  />
                )}
                <Radio.Group
                  value={draft.sameExercise}
                  onChange={(e) =>
                    updateDraft(resource.resource_id, {
                      sameExercise: e.target.value,
                    })
                  }
                >
                  <Space direction="vertical">
                    <Radio
                      value={true}
                      disabled={notSameExerciseReason(resource) !== null}
                    >
                      Es el mismo ejercicio
                      <br />
                      <Text type="secondary">
                        Quienes ya lo terminaron lo ven terminado también en
                        el recurso nuevo.
                      </Text>
                    </Radio>
                    <Radio value={false}>
                      Es otro contenido
                      <br />
                      <Text type="secondary">
                        Quienes ya completaron la tarea la conservan con su
                        nota. El resto la hace con el contenido nuevo.
                      </Text>
                    </Radio>
                  </Space>
                </Radio.Group>
              </div>
            )}

            {resource.in_flight_attempt_count > 0 && (
              <div style={{ marginTop: 8 }}>
                <Checkbox
                  checked={draft.acknowledgeInFlight}
                  onChange={(e) =>
                    updateDraft(resource.resource_id, {
                      acknowledgeInFlight: e.target.checked,
                    })
                  }
                >
                  Confirmo que hay un intento en curso y aun así quiero
                  continuar
                </Checkbox>
              </div>
            )}
          </div>
        );
      })}
    </Modal>
  );
};

export default ResourceHomeworkImpactModal;
